import React, { useEffect, useState } from 'react';

import logo from '../../../assets/spectrum-reach-logo-ltbkg.svg';
import logoDark from '../../../assets/spectrum-reach-logo-dkbkg.svg';
import { KiteIcon } from '@kite/react-kite';
import { ReachButton } from '../../../components/ReachButton/ReachButton';
import { graphql, useStaticQuery } from 'gatsby';
import { sortMenuItems } from '../../../shared/sortMenuItems';
import { useLayoutContext } from '../../../providers/LayoutProvider';
import { NavMenuItem } from '../../../models/NavMenuItem';
import { FloatingMenuArrow } from '../../../components/FloatingMenuArrow/FloatingMenuArrow';
import { useKeyPress } from '../../../shared/useKeyPress';
import { SmartLink } from '../../../components/SmartLink/SmartLink';
import { ParentNodeProvider } from '../../../providers/ParentNodeProvider';
import './NavigationBar.scss';

export const NavigationBar = ({ darkBgLogo }) => {
    const { setSidebarExpanded, setSearchOpen } = useLayoutContext();
    const [nav, setNav] = useState<NavMenuItem[]>([]);
    const [cta, setCta] = useState<NavMenuItem>({ name: ``, url: `` });
    const [logoUrl, setLogoUrl] = useState(`/`);
    const [hideSearchBtn, setHideSearchBtn] = useState(false);

    const menuData = useStaticQuery(graphql`
        query {
            allMenuItems(filter: { menu_name: { eq: "main" } }) {
                nodes {
                    title
                    id
                    url
                    weight
                    childrenMenuItems {
                        id
                        title
                        weight
                        url
                    }
                }
            }
        }
    `);

    if (!nav.length && !cta.name) {
        const rawMenuItems = [
            ...menuData.allMenuItems.nodes.sort(sortMenuItems),
        ];

        const rLogoIdx = rawMenuItems.findIndex(
            (rItem) => rItem.title === `Spectrum Reach Logo`
        );

        if (rLogoIdx > -1) {
            const [reachLogo] = rawMenuItems.splice(rLogoIdx, 1);
            setLogoUrl(reachLogo.url);
        }

        const parentItems = rawMenuItems
            .filter((item) => item.childrenMenuItems.length)
            .sort(sortMenuItems);

        const menuItems: NavMenuItem[] = parentItems.map((item) => ({
            name: item.title,
            expanded: false,
            url: item.url,
            items: item.childrenMenuItems.map((cItem) => ({
                name: cItem.title,
                url: cItem.url,
            })),
        }));

        for (const item of menuItems) {
            rawMenuItems.splice(
                rawMenuItems.findIndex((rItem) => rItem.title === item.name),
                1
            );
            for (const subItem of item.items) {
                rawMenuItems.splice(
                    rawMenuItems.findIndex(
                        (rItem) => rItem.title === subItem.name
                    ),
                    1
                );
            }
        }

        for (const item of rawMenuItems) {
            menuItems.push({
                name: item.title,
                url: item.url,
            });
        }

        const lastItem = menuItems.pop();
        setCta(lastItem);
        setNav(menuItems);
    }

    const setNavHoverState = (expanded, item) => {
        if (expanded) {
            nav.forEach((nItem) => (nItem.expanded = false));
        }
        item.expanded = expanded;
        setNav([...nav]);
    };

    const escPressed = useKeyPress(`Escape`);

    useEffect(() => {
        if (escPressed) {
            nav.forEach((item) => (item.expanded = false));
            setNav([...nav]);
        }
    }, [escPressed]);

    useEffect(() => {
        if (window.location.pathname === `/search`) {
            setHideSearchBtn(true);
        } else {
            setHideSearchBtn(false);
        }
    }, []);

    return (
        <ParentNodeProvider nodeName={undefined} nodeType="Navigation">
            <nav className="kite-container main-navigation nav-header">
                <SmartLink className="logo" href={logoUrl}>
                    <img
                        className="logo"
                        src={darkBgLogo ? logoDark : logo}
                        alt="Spectrum Reach logo"
                    />
                </SmartLink>
                <ul className="links">
                    {nav.map((navItem, idx) => (
                        <li
                            key={`item-${idx}`}
                            onMouseLeave={
                                navItem.items
                                    ? () => setNavHoverState(false, navItem)
                                    : () => true
                            }
                        >
                            {navItem.items && (
                                <>
                                    <SmartLink
                                        href={navItem.url}
                                        aria-expanded={navItem.expanded}
                                        aria-controls={`nav-${idx}`}
                                        role="button"
                                        className={
                                            navItem.expanded
                                                ? `menu-open`
                                                : undefined
                                        }
                                        onMouseEnter={() => {
                                            navItem.expanded = true;
                                            setNav([...nav]);
                                        }}
                                        onTouchStart={(evt) => {
                                            evt.preventDefault();
                                            navItem.timer = Date.now();
                                        }}
                                        onTouchEnd={(evt) => {
                                            if (
                                                !navItem.timer ||
                                                Date.now() - navItem.timer <
                                                    1000
                                            ) {
                                                return true;
                                            } else {
                                                evt.preventDefault();
                                            }
                                        }}
                                        onKeyDown={(evt) => {
                                            if (evt.key === ` `)
                                                evt.preventDefault(); //prevents default behavior of page scrolling on spacebar
                                        }}
                                        onKeyUp={(evt) => {
                                            if (evt.key === ` `) {
                                                navItem.expanded =
                                                    !navItem.expanded;
                                                setNav([...nav]);
                                            }
                                        }}
                                    >
                                        {navItem.name}
                                        {navItem.expanded && (
                                            <KiteIcon
                                                icon="ki-chevron-up"
                                                size="0.75rem"
                                            />
                                        )}
                                        {!navItem.expanded && (
                                            <KiteIcon
                                                icon="ki-chevron-down"
                                                size="0.75rem"
                                            />
                                        )}
                                    </SmartLink>
                                    <h4 className="sr-only">
                                        Related links for {navItem.name}
                                    </h4>
                                    <FloatingMenuArrow
                                        hidden={!navItem.expanded}
                                    />
                                    <ul
                                        hidden={!navItem.expanded}
                                        id={`nav-${idx}`}
                                        onBlur={(evt) => {
                                            const elem =
                                                document.getElementById(
                                                    `nav-${idx}`
                                                );
                                            if (
                                                elem.contains(evt.relatedTarget)
                                            ) {
                                                return;
                                            }
                                            navItem.expanded = false;
                                            setNav([...nav]);
                                        }}
                                    >
                                        {navItem.items.map((subitem, idx) => (
                                            <li key={`subitem-${idx}`}>
                                                <SmartLink
                                                    href={subitem.url}
                                                    onKeyDown={(evt) => {
                                                        if (
                                                            evt.key === ` ` &&
                                                            navItem.expanded
                                                        ) {
                                                            evt.preventDefault(); //prevents default behavior of page scrolling on spacebar
                                                        }
                                                    }}
                                                >
                                                    {subitem.name}
                                                </SmartLink>
                                            </li>
                                        ))}
                                    </ul>
                                </>
                            )}
                            {!navItem.items && (
                                <SmartLink href={navItem.url}>
                                    {navItem.name}
                                </SmartLink>
                            )}
                        </li>
                    ))}
                </ul>
                <div className="right-btns">
                    <ReachButton buttonStyle="primary" href={cta.url}>
                        {cta.name}
                    </ReachButton>
                    <button
                        className="sidebar-btn"
                        onClick={() => {
                            setSidebarExpanded(true);
                            setSearchOpen(true);
                        }}
                        aria-label="Open mobile navigation"
                    >
                        <KiteIcon icon="ki-menu" size="1.5rem" />
                    </button>
                    {!hideSearchBtn && (
                        <button
                            className="search-btn"
                            onClick={() => {
                                setSearchOpen(true);
                            }}
                            aria-label="Open search"
                        >
                            <KiteIcon icon="ki-search" size="1.5rem" />
                        </button>
                    )}
                </div>
            </nav>
        </ParentNodeProvider>
    );
};
